/**
 * Comment API — Comments MVP (2026-05-22)
 *
 * Typed wrappers for all 5 comment endpoints.
 * Follows the evaluationSearchApi.ts style: thin axios wrappers returning
 * strongly-typed response objects.
 */

import { apiClient } from './api';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Visibility levels for comments. */
export type CommentVisibility = 'private' | 'channel' | 'public';

/** A user who can be @-mentioned in a comment (eval participant / channel member). */
export interface MentionableUser {
  user_id: string;
  display_name: string;
  /** Channel role — used to rank/label autocomplete results. */
  role?: string;
}

/** A resolved @mention returned by the backend on a comment. */
export interface CommentMention {
  user_id: string;
  display_name: string;
}

/** A single comment including nested replies (depth 1 only). */
export interface EvaluationComment {
  id: string;
  evaluation_id: string;
  author_user_id: string;
  author_display_name: string | null;
  author_role: string | null;
  parent_comment_id: string | null;
  visibility: CommentVisibility;
  /**
   * Empty string when deleted=true (body is hidden).
   * Render a placeholder in that case.
   */
  body: string;
  video_timestamp_sec: number | null;
  phase_label: string | null;
  created_at: string;
  edited_at: string | null;
  /** True when the comment has been soft-deleted. */
  deleted: boolean;
  /** One-level nested replies. Always [] for reply comments. */
  replies: EvaluationComment[];
  /**
   * Resolved @mentions for this comment. Populated by the backend from
   * the @token text in the body. Absent on older comments.
   */
  mentions?: CommentMention[];
}

export interface CreateCommentPayload {
  body: string;
  visibility?: CommentVisibility;
  parent_comment_id?: string | null;
  video_timestamp_sec?: number | null;
  phase_label?: string | null;
}

export interface UpdateCommentPayload {
  body: string;
}

export interface ReportCommentPayload {
  reason: string;
}

export interface ListCommentsParams {
  limit?: number;
  offset?: number;
}

// ─── API wrappers ─────────────────────────────────────────────────────────────

/**
 * Fetch active (threaded) comments for an evaluation.
 * GET /api/evaluations/{eval_id}/comments
 */
export async function listComments(
  evalId: string,
  params?: ListCommentsParams
): Promise<EvaluationComment[]> {
  const { data } = await apiClient.get<EvaluationComment[]>(
    `/evaluations/${evalId}/comments`,
    { params }
  );
  return data;
}

/**
 * Post a new comment on an evaluation.
 * POST /api/evaluations/{eval_id}/comments
 */
export async function createComment(
  evalId: string,
  payload: CreateCommentPayload
): Promise<EvaluationComment> {
  const { data } = await apiClient.post<EvaluationComment>(
    `/evaluations/${evalId}/comments`,
    payload
  );
  return data;
}

/**
 * Edit the body of a comment (author only).
 * PATCH /api/comments/{comment_id}
 */
export async function updateComment(
  commentId: string,
  payload: UpdateCommentPayload
): Promise<EvaluationComment> {
  const { data } = await apiClient.patch<EvaluationComment>(
    `/comments/${commentId}`,
    payload
  );
  return data;
}

/**
 * Soft-delete a comment (author or coach/admin).
 * DELETE /api/comments/{comment_id}
 */
export async function deleteComment(commentId: string): Promise<EvaluationComment> {
  const { data } = await apiClient.delete<EvaluationComment>(
    `/comments/${commentId}`
  );
  return data;
}

/**
 * File a moderation report against a comment.
 * POST /api/comments/{comment_id}/report
 */
export async function reportComment(
  commentId: string,
  payload: ReportCommentPayload
): Promise<{ detail: string }> {
  const { data } = await apiClient.post<{ detail: string }>(
    `/comments/${commentId}/report`,
    payload
  );
  return data;
}

// ─── Recent coach comments ────────────────────────────────────────────────────

/**
 * A single item from GET /api/users/me/comments/recent.
 * Represents a comment authored by a coach on one of the current user's evaluations.
 */
export interface RecentCoachComment {
  comment_id: string;
  evaluation_id: string;
  comment_text: string;
  author_id: string;
  author_name: string;
  created_at: string;
}

/**
 * Fetch the most recent coach comments on the authenticated user's evaluations.
 * GET /api/users/me/comments/recent?limit=N
 * Default limit: 5; max: 50; min: 1.
 * Sorted newest first.
 */
export async function getRecentCoachComments(
  limit = 5
): Promise<RecentCoachComment[]> {
  const { data } = await apiClient.get<RecentCoachComment[]>(
    '/users/me/comments/recent',
    { params: { limit } }
  );
  return data;
}
